import { observable, computed, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { uniq } from 'lodash-es'
import { validationContext, func, pattern } from 'validx'
import { type InvitationStore } from '@/features/iam/invitations/InvitationStore'
import { type TeamRoleStore } from '@/features/iam/teams/TeamRoleStore'
import { type Team } from '@/features/iam/teams/Team'
import { Id } from '@/infra/id'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { extractMessageFromError } from '@/utils/error-util'

interface InviteResult {
  email: string
  error?: Error
}

export class InviteTeamMemberModalViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor email = ''
  @observable accessor teamRoleId = ''

  readonly validation = validationContext(this, {
    email: [
      func(
        () => this.emailAddresses.length > 0,
        'Please enter at least one email address',
      ),
      func(() => {
        const fn = pattern('email')
        const invalidEmails = this.emailAddresses
          .map((e) => {
            const isValidEmail =
              fn({ obj: this, field: 'email', value: e }) === true
            return isValidEmail ? false : e
          })
          .filter(Boolean)
        return (
          invalidEmails.length === 0 ||
          `The following emails are invalid: ${invalidEmails.join(', ')}`
        )
      }),
      func(() => {
        const teamMemberEmails = this.currentTeamMemberEmailAddresses
        const alreadyExistsEmails = this.emailAddresses.filter((email) =>
          teamMemberEmails.has(email),
        )
        return (
          alreadyExistsEmails.length === 0 ||
          `The following emails already exist in this team: ${alreadyExistsEmails.join(
            ', ',
          )}`
        )
      }),
    ],
  })

  constructor(
    private readonly _invitationStore: InvitationStore,
    private readonly _teamRoleStore: TeamRoleStore,
    private readonly _team: Team,
    private readonly _getTeamMembers: () => TeamMember[],
    private readonly _reportInvitationSent: () => Promise<void>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @computed
  get teamRoles() {
    return this._teamRoleStore
      .forWorkspace(this._team.workspace.id)
      .filter((role) => !role.isOwnerRole)
  }

  @computed
  get teamRoleOptions() {
    return this.teamRoles.map((role) => ({
      id: Id.assert(role.id),
      name: role.name,
    }))
  }

  /**
   * Fetches the team roles, then shows the dialog. Without roles the dialog
   * still opens, with an empty role picker.
   */
  async show(teamRoleId: string | null) {
    await this._teamRoleStore
      .listWorkspaceTeamRoles(this._team.workspace.id)
      .catch(() => undefined)

    runInAction(() => {
      this.validation.reset()
      this.email = ''
      const role = this.teamRoles.find((x) => x.id === teamRoleId)
      if (role && !role.isOwnerRole && teamRoleId) {
        this.teamRoleId = teamRoleId
      } else {
        const firstRole = this.teamRoles[0]
        this.teamRoleId = teamRoleId || (firstRole && Id.assert(firstRole.id))
      }
      this.modal.show()
    })
  }

  @computed
  get currentTeamMemberEmailAddresses() {
    return new Set(this._getTeamMembers().map(({ member }) => member.email))
  }

  @computed
  get emailAddresses() {
    return uniq(
      this.email
        .split(/[,\n]+/g)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    )
  }

  sendInvite = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const emails = this.emailAddresses
    const results: InviteResult[] = await Promise.all(
      emails.map((email) =>
        this._invitationStore
          .inviteToTeam({
            email,
            workspaceId: this._team.workspace.id,
            teamId: this._team.id,
            teamRoleId: this.teamRoleId,
          })
          .then(
            () => ({ email }),
            (err: Error) => ({ email, error: err }),
          ),
      ),
    )

    const failed = results.filter((r) => r.error)
    if (failed.length === 0) {
      this.modal.close()
      // The pending list keeps its previous contents when the refetch fails.
      await this._reportInvitationSent().catch(() => undefined)

      return
    }

    runInAction(() => {
      this.email = failed.map((f) => f.email).join(', ')
    })
    this.modal.close()
    const message = failed
      .map((f) => extractMessageFromError(f.error))
      .join('. ')
    this._createAlertErrorHandler('Sending invitations failed')(
      new Error(message),
    )
  })
}
